import React, { useState } from 'react';
import useModal from './modalHook';
import AddRiskTypeModal from '../views/shared/settings/insuranceConfiguration/AddRiskTypePage';
import {
  createNewInsurancePortfolio,
  createNoClaimDiscount,
  editMotorInsuranceConfiguration,
  getAllRiskTypes,
  udptadeMotorInsuranceConfigurationStatus,
  updateNoClaimDiscount,
  updateNoClaimDiscountStatus,
  updatePortfolioById,
  updatePortfolioStatus,
  updateRiskTypeStatus
} from '../api/requests';
import moment from 'moment';
import AddNoClaimDiscountModal, {
  IDiscountRate
} from '../views/shared/settings/insuranceConfiguration/AddNoClaimDiscountModal';
import AddProductModal, {
  ICoverageTypeConfiguration
} from '../views/shared/settings/insuranceConfiguration/AddProductModal';
import useUserAccount from './userAccountHook';
import { CircleCheck, CircleX, Edit, EllipsisVerticalIcon, Trash } from 'lucide-react';
import { IInsuranceType } from '../views/shared/settings/insuranceConfiguration/AddInsuranceTypePage';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import PopOnMe from '../components/elements/poponme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import useRoute from './routesHook';
import { getFormattedDate, showToast } from '../utils/miscFunctions';
import AddDiscountRateModal from '../views/shared/settings/insuranceConfiguration/AddNoClaimDiscountModal';
import { GTableActionColumn } from '../components/elements/GTable/GTable';
import { IRequestResponse } from '../resources/interfaces';

const useInsuranceConfiguration = () => {
  const { user } = useUserAccount();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const InsuranceConfigurationModal = ModalComponent;
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const queryClient = useQueryClient();
  const [selectedTabItem, setSelectedTabItem] = useState<string>('Risk type');
  const [riskTypes, setRiskTypes] = useState([])
  const [loading, setLoading] = useState<boolean>(true);


  const getRiskTypes = async () => {
    getAllRiskTypes()
      .then((response: any) => {
        setRiskTypes(response);
      })
      .catch((error: any) => {
        console.log('error', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useQuery({ queryKey: ['getRiskTypes', riskTypes], queryFn: getRiskTypes });

  const callEditActions = (type: any) => {
    switch (selectedTabItem) {
      case 'Insurance type':
        return navigate(`${getDashboardBase()}/insurance-configuration/insurance-type?action=edit&id=${type.id}`)
      case 'Risk type':
        return navigate(`${getDashboardBase()}/insurance-configuration/insurance-risk-type?action=edit&id=${type.id}`);

      default:
        break;
    }
  };

  const callUpdateStatusApis = (type: any) => {
    switch (selectedTabItem) {
      case 'Insurance type':
        return udptadeMotorInsuranceConfigurationStatus(type.id, !type.active)
          .then((response:IRequestResponse) => {
            closeModal();
            showToast.success(response?.message || 'Status updated Successful');
            queryClient.invalidateQueries({ queryKey: ['getMotorInsuranceConfigurations'] });
          })
          .catch((error: any) => {
            console.log(error);
            closeModal();
            showToast.error(error?.description ?? 'Could not update the status. Please try again.');
          });
      case 'Risk type':
        return updateRiskTypeStatus(type.id, !type.active)
          .then((response:IRequestResponse) => {
            closeModal();
            showToast.success(response?.message || 'Status updated Successful');
            queryClient.invalidateQueries({ queryKey: ['getRiskTypes'] });
          })
          .catch((error: any) => {
            console.log(error);
            closeModal();
            showToast.error(error?.description ?? 'Could not update the status. Please try again.');
          });
      case 'Coverage type':
        return updatePortfolioStatus(type.id, !type.active)
          .then((response:IRequestResponse) => {
            closeModal();
            showToast.success(response?.message || 'Status updated Successful');
            queryClient.invalidateQueries({ queryKey: ['getPortfolios'] });
          })
          .catch((error: any) => {
            console.log(error);
            closeModal();
            showToast.error(error?.description ?? 'Could not update the status. Please try again.');
          });
      case 'No claim discount':
        return updateNoClaimDiscountStatus(type.id, !type.active)
          .then(() => {
            closeModal();
            showToast.success('Status updated Successful');
            queryClient.invalidateQueries({ queryKey: ['getAllNoClaimDiscounts'] });
          })
          .catch((error: any) => {
            console.log(error);
            closeModal();
            showToast.error(error?.description ?? 'Could not update the status. Please try again.');
          });

      default:
        break;
    }
  };

  const getTableRowActions = (type: any) => {
    const actions = [
      {
        label: type.active ? 'Deactivate' : 'Activate',
        icon: type.active ? <CircleX className="h-4 w-4 " /> : <CircleCheck className="h-4 w-4" />,
        callback: () => updateStatus(type)
      }
    ];

    if ((selectedTabItem !== "Coverage type" && selectedTabItem !== "No claim discount")) {
      actions.push(
        {
          label: 'Edit',
          icon: <Edit className="h-4 w-4 " />,
          callback: () => callEditActions(type)
        });
    }

    if (selectedTabItem === "Coverage type") {
      actions.push(
        {
          label: 'Edit',
          icon: <Edit className="h-4 w-4 " />,
          callback: () => AddCoverageType(type)
        });
    }


    return actions;
  };

  const getTableRowActionsNoClaimDiscount = (type: any) => {
    const actions = [
      {
        label: type.active ? 'Deactivate' : 'Activate',
        icon: type.active ? <CircleX className="h-4 w-4 " /> : <CircleCheck className="h-4 w-4" />,
        callback: () => updateStatus(type)
      },
      {
        label: 'Edit discount',
        icon: <Edit className="h-4 w-4 " />,
        callback: () => selectedTabItem == "No claim discount" && AddNoClaimDiscount(riskTypes, type)
      }
    ];



    return actions;
  };

  const columnFirstTable = {
    Header: 'Charges & Rates',
    columns: [
      {
        Header: 'Base Premium',
        accessor: 'basePremium',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Admin Charge',
        accessor: 'adminCharge',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Operational Expense',
        accessor: 'operationalExpense',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Motor Contribution',
        accessor: 'motorContribution',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Sticker Fee',
        accessor: 'stickerFee',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Excess Rate',
        accessor: 'excessRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Own Damage Rate',
        accessor: 'ownDamageRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Third Party Fire & Theft Rate',
        accessor: 'thirdPartyFireTheftRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Third Party Injury',
        accessor: 'thirdPartyInjury',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Third Party Property Damage Limit',
        accessor: 'thirdPartyPropertyDamageLimit',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Umbrella Limit Rate',
        accessor: 'umbrellaLimitRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Additional Peril Charge',
        accessor: 'additionalPerilCharge',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Brown Card Certificate',
        accessor: 'brownCardCertificate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'ECOWAS Peril Charge',
        accessor: 'ecowasPerilCharge',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Educational Fee',
        accessor: 'educationalFee',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Extra Seat Charge',
        accessor: 'extraSeatCharge',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Extra TP Property Damage Limit Rate',
        accessor: 'extraThirdPartyPropertyDamageLimitRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'GIA Fee',
        accessor: 'ghanaInsuranceAssociationFee',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Max Seats',
        accessor: 'maximumSeats',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Personal Accident Charge',
        accessor: 'personalAccidentCharge',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Status',
        accessor: 'active',
        Cell: (row: any) => (
          <span
            className={`px-2 py-1 rounded text-white text-xs ${row.value ? 'bg-green-500' : 'bg-red-500'
              }`}>
            {row.value ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        Header: 'Created On',
        accessor: 'createdAt',
        Cell: (row: any) => <span>{row.value ? getFormattedDate({ date: row.value }) : '-'}</span>
      },
      {
        Header: 'Updated On',
        accessor: 'updatedAt',
        Cell: (row: any) => <span>{row.value ? getFormattedDate({ date: row.value }) : '-'}</span>
      },
      {
        Header: "Action",
        id: 'Action',
        Cell: (row: any) => {
          if (getTableRowActions(row.row.original).length > 0) return (
            <GTableActionColumn actions={getTableRowActions(row.row.original)} />
          );
        }
      }
    ]
  };

  const columnSecondTable = {
    Header: 'Main',
    columns: [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Description',
        accessor: 'description',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      }
    ]
  };

  const columnThirdTable = {
    Header: 'Main',
    columns: [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Description',
        accessor: 'description',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Code',
        accessor: 'code',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Status',
        accessor: 'active',
        Cell: (row: any) => (
          <span
            className={`px-2 py-1 rounded text-white text-xs ${row.value ? 'bg-green-500' : 'bg-red-500'
              }`}>
            {row.value ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        Header: 'Base Premium',
        accessor: 'basePremium',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Risk Category',
        accessor: 'riskCategory.key',
        Cell: (row: any) => <span>{row.row.original.riskCategory?.displayName || '-'}</span>
      },
      {
        Header: 'Vehicle Use',
        accessor: 'vehicleUse.key',
        Cell: (row: any) => <span>{row.row.original.vehicleUse?.displayName || '-'}</span>
      },
      {
        Header: 'Excess Rate',
        accessor: 'excessRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Sticker Fee',
        accessor: 'stickerFee',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Motor Contribution',
        accessor: 'motorContribution',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Max Seats',
        accessor: 'maximumSeats',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Own Damage Rate',
        accessor: 'ownDamageRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Umbrella Limit Rate',
        accessor: 'umbrellaLimitRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Third Party Injury',
        accessor: 'thirdPartyInjury',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Third Party Property Damage',
        accessor: 'thirdPartyPropertyDamageLimit',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Third Party Fire & Theft Rate',
        accessor: 'thirdPartyFireTheftRate',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Created On',
        accessor: 'createdAt',
        Cell: (row: any) => <span>{moment(row.value).format('DD/MM/YYYY')}</span>
      },
      {
        Header: 'Updated On',
        accessor: 'updatedAt',
        Cell: (row: any) => <span>{moment(row.value).format('DD/MM/YYYY')}</span>
      },
      {
        Header: "Action",
        id: 'Action',
        Cell: (row: any) => {
          return (
            <GTableActionColumn actions={getTableRowActions(row.row.original)} />
          );
        }
      }
    ]
  };

  const columnFourthTable = {
    Header: 'Main',
    columns: [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Code',
        accessor: 'code',
        Cell: (row: any) => <span>{row.value || '-'}</span>
      },
      {
        Header: 'Portfolio',
        accessor: 'portfolio.displayName',
        Cell: (row: any) => <span>{row.row.original.portfolio?.displayName || '-'}</span>
      },
      {
        Header: 'Insurer',
        accessor: 'insurerInfo.name',
        Cell: (row: any) => <span>{row.row.original.insurerInfo?.name || '-'}</span>
      },
      {
        Header: 'Charge Value',
        accessor: 'additionalChargeValue',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Charge Type',
        accessor: 'additionalChargeType.displayName',
        Cell: (row: any) => <span>{row.row.original.additionalChargeType?.displayName || '-'}</span>
      },
      {
        Header: 'Status',
        accessor: 'active',
        Cell: (row: any) => (
          <span
            className={`px-2 py-1 rounded text-white text-xs ${row.value ? 'bg-green-500' : 'bg-red-500'
              }`}>
            {row.value ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        Header: 'Created On',
        accessor: 'createdAt',
        Cell: (row: any) => <span>{new Date(row.value).toLocaleDateString()}</span>
      },
      {
        Header: 'Updated On',
        accessor: 'updatedAt',
        Cell: (row: any) => <span>{new Date(row.value).toLocaleDateString()}</span>
      },
      {
        Header: "Action",
        id: 'Action',
        Cell: (row: any) => {
          if (getTableRowActions(row.row.original).length > 0) return (
            <GTableActionColumn actions={getTableRowActions(row.row.original)} />
          );
        }
      }
    ]
  };

  const columnFithTable = {
    Header: 'Main',
    columns: [
      {
        Header: 'Title',
        accessor: 'title',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Age',
        accessor: 'age',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}</span>
      },
      {
        Header: 'Discount',
        accessor: 'discount',
        Cell: (row: any) => <span>{row.value ? row.value : '-'}%</span>
      },
      {
        Header: 'Risk Type',
        accessor: 'riskType.name',
        Cell: (row: any) => <span>{row.row.original.riskType?.name || '-'}</span>
      },
      {
        Header: 'Risk Code',
        accessor: 'riskType.code',
        Cell: (row: any) => <span>{row.row.original.riskType?.code || '-'}</span>
      },
      {
        Header: 'Vehicle Use',
        accessor: 'vehicleUse.displayName',
        Cell: (row: any) => <span>{row.row.original.vehicleUse?.displayName || '-'}</span>
      },
      {
        Header: 'Status',
        accessor: 'active',
        Cell: (row: any) => (
          <span
            className={`px-2 py-1 rounded text-white text-xs ${row.value ? 'bg-green-500' : 'bg-red-500'
              }`}>
            {row.value ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        Header: 'Created On',
        accessor: 'createdAt',
        Cell: (row: any) => <span>{new Date(row.value).toLocaleDateString()}</span>
      },
      {
        Header: 'Updated On',
        accessor: 'updatedAt',
        Cell: (row: any) => <span>{new Date(row.value).toLocaleDateString()}</span>
      },
      {
        Header: "Action",
        id: 'Action',
        Cell: (row: any) => {
          if (getTableRowActionsNoClaimDiscount(row.row.original).length > 0) return (
            <GTableActionColumn actions={getTableRowActionsNoClaimDiscount(row.row.original)} />
          );
        }
      }
    ]
  };


  const updateStatus = (type: IInsuranceType) => {
    updateModalStates({
      show: true,
      children: (
        <ActionConfirmation
          action={type.active ? "deactivate" : "activate"}
          type={type.active ? "danger" : "success"}
          resourceType={''}
          resourceName={''}
          onClose={closeModal}
          onConfirm={() => {
            type && callUpdateStatusApis(type);
          }}
        />
      ),
      size: 'sm',
      showTitleSection: true
    });
  };

  const AddNoClaimDiscount = (riskTypesList?: any[], type?: any) => {
    const typesToUse = riskTypesList || riskTypes;

    updateModalStates({
      show: true,
      children: (
        <AddDiscountRateModal
          onClose={closeModal}
          type={type}
          riskTypes={typesToUse ?? []}
          onConfirm={(values: IDiscountRate) => {
            !type && createNoClaimDiscount(values)
              .then((response: any) => {
                showToast.success(response?.message || 'No-claim discount created successfully.');
              })
              .catch((error: any) => {
                showToast.error(error?.description ?? 'Could not create the no-claim discount. Please try again.');
                console.log('error', error);
              })
              .finally(() => { closeModal(); queryClient.invalidateQueries({ queryKey: ['getAllNoClaimDiscounts'] }); });

            type && updateNoClaimDiscount(type.id, values)
              .then((response: any) => {
                showToast.success(response?.message || 'No-claim discount updated successfully.');
              })
              .catch((error: any) => {
                showToast.error(error?.description ?? 'Could not update the no-claim discount. Please try again.');
                console.log('error', error);
              })
              .finally(() => { closeModal(); queryClient.invalidateQueries({ queryKey: ['getAllNoClaimDiscounts'] }); });
          }}
        />
      ),
      size: 'sm',
      showTitleSection: true
    });
  };

  const AddCoverageType = (type?: any) => {
    updateModalStates({
      show: true,
      children: (
        <AddProductModal
          onClose={closeModal}
          type={type}

          onConfirm={(values: ICoverageTypeConfiguration) => {
            !type && createNewInsurancePortfolio(values)
              .then((response: any) => {
                showToast.success(response?.message || 'Coverage type created successfully.');
              })
              .catch((error: any) => {
                showToast.error(error?.description ?? 'Could not create the coverage type. Please try again.');
                console.log('error', error);
              })
              .finally(() => { closeModal(); queryClient.invalidateQueries({ queryKey: ['getPortfolios'] }); });

            type && updatePortfolioById(type.id, values)
              .then((response: any) => {
                showToast.success(response?.message || 'Coverage type updated successfully.');
              })
              .catch((error: any) => {
                showToast.error(error?.description ?? 'Could not update the coverage type. Please try again.');
                console.log('error', error);
              })
              .finally(() => { closeModal(); queryClient.invalidateQueries({ queryKey: ['getPortfolios'] }); });
          }}


        />
      ),
      size: 'xl',
      showTitleSection: true,
      title: type ? 'Edit Coverage Type' : 'Add Coverage Type'
    });
  };

  const insuranceConfigTabs = (insuranceConfigurationTab?: string): { label: string; action: () => void } => {
    switch (insuranceConfigurationTab) {
      case "Insurance type":
        return { label: "Add Insurance Type", action: () => navigate(`${getDashboardBase()}/insurance-configuration/insurance-type?action=create`) };
      case "Body type":
        return { label: "Add Body type", action: () => { } };
      case "Risk type":
        return { label: "Add Risk type", action: () => navigate(`${getDashboardBase()}/insurance-configuration/insurance-risk-type?action=create`) };
      case "Coverage type":
        return { label: "Add Coverage type", action: () => AddCoverageType() };
      case "No claim discount":
        return { label: "Add Discount Rate", action: () => AddNoClaimDiscount() };

      default:
        return { label: "Add Risk type", action: () => navigate(`${getDashboardBase()}/insurance-configuration/insurance-risk-type?action=create`) };
    }
  }

  return {
    AddNoClaimDiscount,
    AddCoverageType,
    InsuranceConfigurationModal,
    setSelectedTabItem,
    setLoading,
    columnFirstTable,
    riskTypes,
    loading,
    columnSecondTable,
    columnThirdTable,
    columnFourthTable,
    columnFithTable,
    insuranceConfigTabs
  };
};

export default useInsuranceConfiguration;
